import { create } from 'zustand'

const useCameraStore = create((set) => ({
  // Camera zoom level (0 = default, positive = zoomed in)
  zoomLevel: 0,
  // Target zoom level for smooth transitions
  targetZoomLevel: 0,
  // Initial zoom amount when clicking shop/screen
  initialZoomAmount: 1.2,
  // Maximum zoom level - increased for more freedom
  maxZoom: 3.0,
  // Minimum zoom level - allow zooming out further
  minZoom: -2,
  // Zoom speed for mouse wheel
  zoomSpeed: 0.5,
  // Camera pan offset (x direction: -1 = left, 0 = center, 1 = right)
  panOffset: 0,
  targetPanOffset: 0,
  // Last clicked element to track toggle behavior
  lastClickedElement: null,
  // Flag to override mouse control when element is focused
  isElementFocused: false,
  // Debounce flag to prevent rapid clicks
  isClickDebounced: false,
  // Track which element is currently hovered
  hoveredElement: null,

  // Set hovered element
  setHoveredElement: (elementId) => set({ hoveredElement: elementId }),

  // Set zoom level directly
  setZoomLevel: (level) => set({ zoomLevel: level, targetZoomLevel: level }),

  // Set target zoom for smooth transitions
  setTargetZoom: (target) => set({ targetZoomLevel: target }),

  // Set pan offset
  setPanOffset: (offset) => set({ panOffset: offset, targetPanOffset: offset }),

  // Set target pan for smooth transitions
  setTargetPan: (target) => set({ targetPanOffset: target }),

  // Update current pan offset (for smooth transitions)
  updatePanOffset: (newOffset) => set({ panOffset: newOffset }),

  // Zoom in by initial amount (for click events) - only zoom if not already zoomed
  zoomIn: () => set((state) => {
    // Only zoom if we're not already zoomed in
    if (state.targetZoomLevel < 0.5) {
      return { targetZoomLevel: Math.min(state.initialZoomAmount, state.maxZoom) }
    }
    // If already zoomed, don't zoom further
    return {}
  }),

  // Get camera preset for different elements
  getCameraPreset: (elementId) => {
    // Check if mobile for closer booth view
    const isMobile = window.innerWidth < 768

    const presets = {
      'coffee-shop': {
        zoomLevel: 1.2,
        panOffset: 0,
        focusPoint: [0, 0, 0]
      },
      'contact-booth': {
        zoomLevel: isMobile ? 2.2 : 1.1,  // Much closer on mobile, FPS-style
        panOffset: 6.2,  // Positive to pan right, looking at booth from the left
        focusPoint: [1.8, 0.4, 1.3]
      },
      'default': {
        zoomLevel: 0,
        panOffset: 0,
        focusPoint: [0, 0, 0]
      }
    }
    return presets[elementId] || presets.default
  },

  // Handle click with toggle behavior and camera presets
  handleElementClick: (elementId, customPanDirection = null) => set((state) => {
    // Debounce check - ignore clicks if already debounced
    if (state.isClickDebounced) {
      console.log('Click ignored - debounced')
      return state
    }

    console.log('Click detected:', {
      elementId,
      lastClicked: state.lastClickedElement,
      isZoomed: state.isElementFocused,
      currentZoom: state.targetZoomLevel
    })

    // Clear debounce after 500ms
    setTimeout(() => {
      useCameraStore.setState({ isClickDebounced: false })
    }, 500)

    // Get the preset for this element
    const preset = state.getCameraPreset(elementId)
    const panDirection = customPanDirection !== null ? customPanDirection : preset.panOffset
    const defaultPreset = state.getCameraPreset('default')

    // If clicking 'default' or ground, always zoom out
    if (elementId === 'default') {
      console.log('Zooming OUT to default')
      return {
        ...state,
        isClickDebounced: true,
        targetZoomLevel: defaultPreset.zoomLevel,
        targetPanOffset: defaultPreset.panOffset,
        lastClickedElement: null,
        isElementFocused: false
      }
    }

    // Special handling for contact-booth - NEVER triggers shop
    if (elementId === 'contact-booth') {
      // If booth is already focused, go back to default
      if (state.lastClickedElement === 'contact-booth' && state.isElementFocused) {
        console.log('Toggling booth OFF to default')
        return {
          ...state,
          isClickDebounced: true,
          targetZoomLevel: defaultPreset.zoomLevel,
          targetPanOffset: defaultPreset.panOffset,
          lastClickedElement: null,
          isElementFocused: false
        }
      }
      // From any other state (including shop), just focus on booth
      else {
        console.log('Focusing on booth')
        return {
          ...state,
          isClickDebounced: true,
          targetZoomLevel: preset.zoomLevel,
          targetPanOffset: panDirection,
          lastClickedElement: 'contact-booth',
          isElementFocused: true
        }
      }
    }

    // Handling for coffee-shop
    if (elementId === 'coffee-shop') {
      // If shop is already focused, go back to default
      if (state.lastClickedElement === 'coffee-shop' && state.isElementFocused) {
        console.log('Toggling shop OFF to default')
        return {
          ...state,
          isClickDebounced: true,
          targetZoomLevel: defaultPreset.zoomLevel,
          targetPanOffset: defaultPreset.panOffset,
          lastClickedElement: null,
          isElementFocused: false
        }
      }
      // If booth is focused, first go to default
      else if (state.lastClickedElement === 'contact-booth' && state.isElementFocused) {
        console.log('Booth is focused, going to default first')
        return {
          ...state,
          isClickDebounced: true,
          targetZoomLevel: defaultPreset.zoomLevel,
          targetPanOffset: defaultPreset.panOffset,
          lastClickedElement: null,
          isElementFocused: false
        }
      }
      // Otherwise, focus on shop
      else {
        console.log('Focusing on shop')
        return {
          ...state,
          isClickDebounced: true,
          targetZoomLevel: preset.zoomLevel,
          targetPanOffset: panDirection,
          lastClickedElement: 'coffee-shop',
          isElementFocused: true
        }
      }
    }

    // For any other elements (if any), use the same toggle behavior
    if (state.lastClickedElement === elementId && state.isElementFocused) {
      console.log('Toggling OFF from', elementId)
      return {
        ...state,
        isClickDebounced: true,
        targetZoomLevel: defaultPreset.zoomLevel,
        targetPanOffset: defaultPreset.panOffset,
        lastClickedElement: null,
        isElementFocused: false
      }
    } else {
      console.log('Switching to', elementId)
      return {
        ...state,
        isClickDebounced: true,
        targetZoomLevel: preset.zoomLevel,
        targetPanOffset: panDirection,
        lastClickedElement: elementId,
        isElementFocused: true
      }
    }
  }),

  // Zoom by delta amount (for mouse wheel)
  zoomByDelta: (delta) => set((state) => {
    // Check if mobile portrait mode
    const isMobilePortrait = window.innerWidth < 768 && window.innerHeight > window.innerWidth
    const maxLimit = isMobilePortrait ? 2.5 : state.maxZoom  // Increased to allow booth zoom
    const minLimit = isMobilePortrait ? -0.5 : state.minZoom

    return {
      targetZoomLevel: Math.max(
        minLimit,
        Math.min(maxLimit, state.targetZoomLevel + delta * state.zoomSpeed)
      ),
      // Clear focus state when using mouse wheel
      isElementFocused: false,
      lastClickedElement: null,
      targetPanOffset: 0
    }
  }),

  // Reset zoom to default
  resetZoom: () => set({ zoomLevel: 0, targetZoomLevel: 0 }),

  // Update current zoom level (for smooth transitions)
  updateZoomLevel: (newLevel) => set({ zoomLevel: newLevel }),

  // DOF settings for different zoom states
  dofSettings: {
    default: {  // This is when zoomLevel = 0 (not zoomed, full scene view)
      target: [0, 0, 0],    // Focus on center (sharp default view)
      focalLength: 0.001,   // Minimal DOF (everything sharp)
      bokehScale: 1,        // Almost no blur
      height: 480
    },
    zoomed: {  // This is when zoomLevel > 0 (zoomed in on details)
      target: [0, 0, 8],    // Focus on middle distance
      focalLength: 0.02,    // Very minimal DOF for sharpness
      bokehScale: 1,        // Minimal background blur
      height: 600
    }
  },

  // Get interpolated DOF settings based on zoom
  getDOFSettings: () => {
    const state = useCameraStore.getState()
    const t = Math.min(Math.max(state.zoomLevel / state.maxZoom, 0), 1)
    const defaultDOF = state.dofSettings.default
    const zoomedDOF = state.dofSettings.zoomed

    return {
      target: [
        defaultDOF.target[0] + (zoomedDOF.target[0] - defaultDOF.target[0]) * t,
        defaultDOF.target[1] + (zoomedDOF.target[1] - defaultDOF.target[1]) * t,
        defaultDOF.target[2] + (zoomedDOF.target[2] - defaultDOF.target[2]) * t
      ],
      focalLength: defaultDOF.focalLength + (zoomedDOF.focalLength - defaultDOF.focalLength) * t,
      bokehScale: defaultDOF.bokehScale + (zoomedDOF.bokehScale - defaultDOF.bokehScale) * t,
      height: defaultDOF.height + (zoomedDOF.height - defaultDOF.height) * t
    }
  }
}))

export default useCameraStore